import getMascot, {
  type MascotJobResponse,
  type MascotStatus,
} from '@cartwhl/common/api/getMascot';
import getMascotsByBatch from '@cartwhl/common/api/getMascotsByBatch';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import { waitForAsyncState } from '@cartwhl/common/utils';

import * as globalStores from '../../stores/global';
import type { MascotJobMetadata } from './types';

const POLLING_INTERVAL = 15000;

type Deps = {
  global: typeof globalStores;
};

type State = {
  processId: string | null;
  processName: string;
  email: string;
  batchErrorMessage: string;

  fileMetadata: MascotJobMetadata[];

  status: 'idle' | 'loading' | MascotStatus;
  processedFilesCount: number;
  estimatedProcessingTime: number;

  downloadUrl: string | null;
};

const initialState: State = {
  processId: null,
  processName: '',
  email: '',
  batchErrorMessage: '',
  fileMetadata: [],
  status: 'idle',
  processedFilesCount: 0,
  estimatedProcessingTime: 0,
  downloadUrl: null,
};

const TERMINAL_STATUSES: MascotStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'REJECTED',
];

class BatchMascotProcessStore extends BaseStore<State, Deps> {
  private statusPollingInterval: NodeJS.Timeout | null = null;
  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.resumePollingIfNeeded();
    }
  };

  constructor(deps: Deps) {
    super(initialState, deps);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  reset() {
    this.stopStatusPolling();
    Object.assign(this.state, initialState);
  }

  destroy() {
    this.stopStatusPolling();
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    super.destroy();
  }

  async loadProcess(processId: string) {
    if (this.state.processId === processId && this.state.status !== 'idle') {
      return;
    }

    this.state.processId = processId;
    this.state.status = 'loading';

    let apiKey: string;
    try {
      const apiKeyData = await waitForAsyncState(
        this.deps.global.userStore.state,
        (s) => s.apiKey,
      );
      if (!apiKeyData) {
        globalStores.noticesStore.error(
          'Failed to authenticate. Please try again.',
        );
        this.state.status = 'FAILED';
        return;
      }
      apiKey = apiKeyData.apiKey;
    } catch (err) {
      console.error('Failed to get API key:', err);
      globalStores.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      this.state.status = 'FAILED';
      return;
    }

    const [batchResult, jobsResult] = await Promise.all([
      getMascot(processId, apiKey),
      getMascotsByBatch(processId, apiKey),
    ]);

    if (batchResult.error || !batchResult.data) {
      console.error('Failed to load batch:', batchResult.error?.message);
      globalStores.noticesStore.error(
        batchResult.error?.message || 'Failed to load batch status',
      );
      this.state.status = 'FAILED';
      return;
    }

    this.updateFromBatchResponse(batchResult.data);

    if (jobsResult.data) {
      this.updateFromJobsResponse(jobsResult.data.items);
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- TS narrows to 'loading' but updateFromBatchResponse mutates status
    const status = this.state.status as State['status'];
    if (
      status === 'VALIDATING' ||
      status === 'IN_PROGRESS'
    ) {
      this.startStatusPolling(processId);
    }
  }

  async copyProcessIdToClipboard() {
    if (this.state.processId) {
      await navigator.clipboard.writeText(this.state.processId);
      this.deps.global.noticesStore.info('Copied to clipboard');
    }
  }

  /** Trigger a browser download for a single mascot output video via its direct URL. */
  downloadOutputVideo(file: MascotJobMetadata) {
    const url = file.outputVideoURL;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.name}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  stopPolling() {
    this.stopStatusPolling();
  }

  private updateFromBatchResponse(data: MascotJobResponse) {
    this.state.status = data.status;
    this.state.processName = data.mascotName ?? '';
    this.state.email = data.emailSubscribers?.[0] ?? '';
    this.state.batchErrorMessage = data.errorMessage ?? '';
    this.state.estimatedProcessingTime = data.estimatedSecondsWaitTime ?? 0;
    this.state.downloadUrl = data.downloadURL ?? null;
  }

  private updateFromJobsResponse(items: MascotJobResponse[]) {
    this.state.fileMetadata = items.map((item) => ({
      id: item.mascotID,
      name: item.mascotName ?? item.mascotID,
      status: item.status,
      outputVideoURL: item.outputVideoURL,
      errorMessage: item.errorMessage,
    }));

    const terminalCount = items.filter((m) =>
      TERMINAL_STATUSES.includes(m.status),
    ).length;
    this.state.processedFilesCount = terminalCount;
  }

  private async pollOnce(processId: string): Promise<void> {
    const apiKey = this.deps.global.userStore.state.apiKey.data?.apiKey ?? '';
    const [batchResult, jobsResult] = await Promise.all([
      getMascot(processId, apiKey),
      getMascotsByBatch(processId, apiKey),
    ]);

    if (batchResult.data) {
      this.updateFromBatchResponse(batchResult.data);
    }

    if (jobsResult.data) {
      this.updateFromJobsResponse(jobsResult.data.items);
    }
  }

  private startStatusPolling(processId: string) {
    this.stopStatusPolling();

    this.statusPollingInterval = setInterval(async () => {
      try {
        await this.pollOnce(processId);
      } catch (err) {
        console.warn('Mascot status poll failed, will retry:', err);
      }

      if (TERMINAL_STATUSES.includes(this.state.status as MascotStatus)) {
        this.stopStatusPolling();
      }
    }, POLLING_INTERVAL);
  }

  private async resumePollingIfNeeded() {
    const processId = this.state.processId;
    if (!processId) return;

    const status = this.state.status;
    if (status !== 'VALIDATING' && status !== 'IN_PROGRESS') {
      return;
    }

    try {
      await this.pollOnce(processId);
    } catch (err) {
      console.warn('Mascot status poll on resume failed:', err);
    }

    if (TERMINAL_STATUSES.includes(this.state.status as MascotStatus)) {
      this.stopStatusPolling();
    } else {
      this.startStatusPolling(processId);
    }
  }

  private stopStatusPolling() {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: BatchMascotProcessStore,
  displayName: 'BatchMascotProcessStore',
  staticDeps: { global: globalStores },
});

export type { BatchMascotProcessStore };
